/**
 * Katalógus — A6 of Barna's catalogue brief of 19 October 2026.
 *
 *   > On the catalogue page: last sync time, trigger, who, result, and the
 *   > withheld list with reasons, from sync_runs; the last 20 runs in a
 *   > table. The existing sync button stays and goes through the shared
 *   > module.
 *
 * There was no catalogue page before this run and no button on one: the
 * catalogue was synced from the command line and by the fifteen-minute job.
 * The page is built here with the button on it, and the button calls the
 * `catalogue-sync` Edge Function directly, so it takes the same lock, applies
 * the same gate and writes the same run row as the hourly job and the Sheet
 * menu. That is what "goes through the shared module" means.
 *
 * Staff-facing. No price per dish, no production figure and no customer-facing
 * wording is on this screen, and nothing customer-facing reads it.
 */

import { useCallback, useState } from 'react'

import { catalogueSyncNow, ConsoleError, type CatalogueSyncAnswer } from '../api.ts'
import { useConsole } from '../context.ts'
import { asMoment, Loading, useConsoleData } from '../common.tsx'

interface WithheldDish {
  id: string
  name_hu: string
  reason: string
  reasons?: string[]
}

interface SyncRun {
  id: number
  started_at: string
  finished_at: string | null
  trigger: string | null
  triggered_by: string | null
  sheet_modified_time: string | null
  result: string
  published_count: number | null
  withheld_count: number | null
  inactive_count: number | null
  changed_count: number | null
  withheld: WithheldDish[]
  reasons: string[]
}

interface AllergenHold {
  dish_id: string
  name_hu: string
  held_at: string
  changes: Array<{ field: string; old: string | null; new: string | null }>
}

interface CataloguePage {
  runs: SyncRun[]
  last: SyncRun | null
  interval_minutes: number | null
  settle_minutes: number | null
  running: boolean
  withheld_now: WithheldDish[]
  inactive_now: number
  published_now: number
  catalogue_version: string | null
  allow_unverified: boolean
  dish_count: number
  allergen_holds: AllergenHold[]
  /** Dish photos (DECIDED 20261101): every active dish, its photo and last error. */
  dish_photos?: Array<{
    dish_id: string
    name_hu: string
    thumb: string | null
    source_name: string | null
    updated_at: string | null
    last_error: string | null
  }>
  /** The last photo run, with the files it skipped and why. */
  photo_run?: {
    id: number
    started_at: string
    outcome: string
    reasons: string[]
    detail: {
      skipped?: Array<{ name: string; reason: string }>
      counts?: Record<string, number>
      /** For example *resize needed: run scripts/prepare_dish_photos.mjs*. */
      warnings?: string[]
    }
  } | null
}

/** The six results, as a person reads them. */
const RESULT_HU: Record<string, string> = {
  published: 'közzétéve',
  unchanged: 'nincs változás',
  skipped_settling: 'kihagyva (a tábla épp módosult)',
  refused: 'elutasítva',
  failed: 'hiba',
  running: 'fut',
  ok: 'közzétéve',
  skipped: 'kihagyva',
}

const TRIGGER_HU: Record<string, string> = {
  cron: 'időzített',
  console: 'konzol',
  sheet: 'táblázat menü',
  cli: 'parancssor',
}

/**
 * The photo run's warnings (rc5_live item 4, 3 November 2026). With
 * `PHOTO_CONVERTER=prebuilt` a new or changed Drive photo is not converted:
 * the sync keeps the old photo and records *resize needed* here, and until
 * now staff could see it only in `sync_runs`.
 */
export function PhotoRunWarnings(props: { warnings: readonly string[] | undefined }): JSX.Element | null {
  const warnings = props.warnings ?? []
  if (warnings.length === 0) return null
  return (
    <div className="note" data-testid="photo-warnings">
      Figyelmeztetések az utolsó fotószinkronból:
      <ul>
        {warnings.map((warning, i) => (
          <li key={i}>{warning}</li>
        ))}
      </ul>
    </div>
  )
}

const resultText = (value: string | null): string => RESULT_HU[value ?? ''] ?? value ?? '—'
const triggerText = (value: string | null): string => TRIGGER_HU[value ?? ''] ?? value ?? '—'

/** The one place this screen turns the function's answer into a sentence.
 *  The Hungarian is the Sheet menu's, so the two read alike. */
function answerText(answer: CatalogueSyncAnswer): string {
  if (answer.status === 'published') {
    return `${answer.published} fogás közzétéve, ${answer.withheld.length} visszatartva (${answer.inactive} nem aktív).`
  }
  if (answer.status === 'unchanged') return 'Nincs változás a legutóbbi közzététel óta.'
  if (answer.status === 'running') return 'Egy közzététel már fut. Próbálja újra egy perc múlva.'
  if (answer.status === 'forbidden') return 'Ehhez nincs jogosultsága.'
  return `A tábla nem közzétehető: ${answer.reason ?? 'ismeretlen ok'}. Az oldalon a legutóbbi jó állapot maradt.`
}

export function Catalogue(): JSX.Element {
  const { config, session } = useConsole()
  const { data, error, loading, reload } = useConsoleData<CataloguePage>('catalogue', { limit: 20 })
  const [busy, setBusy] = useState(false)
  const [answer, setAnswer] = useState<CatalogueSyncAnswer | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const publish = useCallback(async () => {
    setBusy(true)
    setFailure(null)
    setAnswer(null)
    try {
      setAnswer(await catalogueSyncNow({ config, session }))
    } catch (cause) {
      setFailure(cause instanceof ConsoleError ? cause.message : 'A közzététel nem sikerült.')
    } finally {
      setBusy(false)
      reload()
    }
  }, [config, session, reload])

  const last = data?.last ?? null

  return (
    <section data-testid="view-catalogue">
      <h2>Katalógus</h2>
      <p className="lead">
        A katalógus óránként frissül a táblázatból. Itt látja, mikor futott utoljára, ki indította,
        mi lett az eredménye, és mely fogások maradtak ki — és itt tudja azonnal közzétenni.
      </p>

      <Loading loading={loading} error={error} />

      {data !== null && (
        <>
          <div className="row" data-testid="catalogue-publish">
            <button type="button" onClick={publish} disabled={busy}>
              {busy ? 'Közzététel…' : 'Közzététel most'}
            </button>
            {data.running && <span className="tag warn">Egy közzététel épp fut.</span>}
            {answer !== null && (
              <span data-testid="catalogue-publish-answer">{answerText(answer)}</span>
            )}
            {failure !== null && <span className="tag stop">{failure}</span>}
          </div>

          {answer?.drive_problem != null && (
            <p className="note" data-testid="catalogue-drive-problem">
              A Drive nem adta meg a táblázat módosítási idejét: {answer.drive_problem}. A szinkron
              ettől még lefutott, csak a „nincs változás” ellenőrzés maradt ki.
            </p>
          )}

          <h3>Legutóbbi szinkron</h3>
          <dl data-testid="catalogue-last">
            <dt>Ideje</dt>
            <dd>{asMoment(last?.finished_at ?? last?.started_at ?? null)}</dd>
            <dt>Indította</dt>
            <dd>
              {triggerText(last?.trigger ?? null)}
              {last?.triggered_by ? ` · ${last.triggered_by}` : ''}
            </dd>
            <dt>Eredmény</dt>
            <dd data-testid="catalogue-last-result">{resultText(last?.result ?? null)}</dd>
            <dt>Közzétett fogás</dt>
            <dd>{last?.published_count ?? data.published_now}</dd>
            <dt>Visszatartva</dt>
            <dd>{last?.withheld_count ?? data.withheld_now.length}</dd>
            <dt>Nem aktív</dt>
            <dd data-testid="catalogue-last-inactive">{last?.inactive_count ?? data.inactive_now}</dd>
            <dt>Változott mező</dt>
            <dd>{last?.changed_count ?? 0}</dd>
            <dt>Gyakoriság</dt>
            <dd>
              {data.interval_minutes ?? '—'} percenként, {data.settle_minutes ?? '—'} perc
              várakozással a tábla utolsó módosítása után
            </dd>
          </dl>

          {(last?.reasons ?? []).length > 0 && (
            <p className="note" data-testid="catalogue-last-reasons">
              Az elutasítás oka: {(last?.reasons ?? []).join(' · ')}. Az oldalon a legutóbbi jó
              állapot maradt.
            </p>
          )}

          <h3>Visszatartott fogások</h3>
          <p className="note">
            Csak azok a fogások, amelyek a táblázatban aktívak, de valami hiányzik hozzájuk.
            {data.inactive_now > 0 &&
              ` A táblázatban nem aktívra állított ${data.inactive_now} fogás nem ide tartozik: azokkal nincs tennivaló.`}
          </p>
          {data.withheld_now.length === 0 ? (
            <p className="note">Nincs visszatartott fogás.</p>
          ) : (
            <table data-testid="catalogue-withheld">
              <thead>
                <tr>
                  <th>Azonosító</th>
                  <th>Név (HU)</th>
                  <th>Ok</th>
                </tr>
              </thead>
              <tbody>
                {data.withheld_now.map((dish) => (
                  <tr key={dish.id}>
                    <td>{dish.id}</td>
                    <td>{dish.name_hu}</td>
                    <td>{(dish.reasons ?? [dish.reason]).join(' · ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {data.allergen_holds.length > 0 && (
            <>
              <h3>Allergén-visszavonás</h3>
              <p className="note">
                Ezek a fogások azért nincsenek a kínálatban, mert az allergénadatuk új hitelesítő
                vagy új hitelesítési dátum nélkül változott. Akkor kerülnek vissza, amikor egy
                szinkron a változást újrahitelesítéssel együtt látja.
              </p>
              <table data-testid="catalogue-allergen-holds">
                <thead>
                  <tr>
                    <th>Azonosító</th>
                    <th>Név (HU)</th>
                    <th>Mikortól</th>
                    <th>Mi változott</th>
                  </tr>
                </thead>
                <tbody>
                  {data.allergen_holds.map((hold) => (
                    <tr key={hold.dish_id}>
                      <td>{hold.dish_id}</td>
                      <td>{hold.name_hu}</td>
                      <td>{asMoment(hold.held_at)}</td>
                      <td>
                        {hold.changes
                          .map((change) => `${change.field}: ${change.old ?? '—'} → ${change.new ?? '—'}`)
                          .join(' · ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <h3>Az utolsó 20 futás</h3>
          <table data-testid="catalogue-runs">
            <thead>
              <tr>
                <th>Indult</th>
                <th>Indította</th>
                <th>Ki</th>
                <th>Eredmény</th>
                <th>Közzétéve</th>
                <th>Visszatartva</th>
                <th>Nem aktív</th>
                <th>Változott</th>
              </tr>
            </thead>
            <tbody>
              {data.runs.map((run) => (
                <tr key={run.id}>
                  <td>{asMoment(run.started_at)}</td>
                  <td>{triggerText(run.trigger)}</td>
                  <td>{run.triggered_by ?? '—'}</td>
                  <td>
                    {resultText(run.result)}
                    {run.reasons.length > 0 ? ` — ${run.reasons.join(' · ')}` : ''}
                  </td>
                  <td>{run.published_count ?? '—'}</td>
                  <td>{run.withheld_count ?? '—'}</td>
                  <td>{run.inactive_count ?? '—'}</td>
                  <td>{run.changed_count ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Photo prompt §6 (DECIDED 20261101): read-only. The photos come from
              the Drive folder through the catalogue sync. */}
          <h3>Fotók</h3>
          {data.photo_run ? (
            <p className="note" data-testid="photo-run">
              Utolsó fotószinkron: {asMoment(data.photo_run.started_at)} · {resultText(data.photo_run.outcome)}
              {data.photo_run.detail.counts
                ? ` · új ${data.photo_run.detail.counts['added'] ?? 0}, csere ${data.photo_run.detail.counts['replaced'] ?? 0}, ` +
                  `törölve ${data.photo_run.detail.counts['removed'] ?? 0}, kihagyva ${data.photo_run.detail.counts['skipped'] ?? 0}, ` +
                  `hiba ${data.photo_run.detail.counts['failed'] ?? 0}`
                : ''}
              {data.photo_run.reasons.length > 0 ? ` — ${data.photo_run.reasons.join(' · ')}` : ''}
            </p>
          ) : (
            <p className="note">Még nem futott fotószinkron.</p>
          )}
          {(data.photo_run?.detail.skipped ?? []).length > 0 && (
            <ul data-testid="photo-skipped">
              {data.photo_run!.detail.skipped!.map((skip) => (
                <li key={skip.name}>{skip.name}: {skip.reason}</li>
              ))}
            </ul>
          )}
          <PhotoRunWarnings warnings={data.photo_run?.detail.warnings} />
          <table data-testid="dish-photos">
            <thead>
              <tr>
                <th>Fogás</th>
                <th>Fotó</th>
                <th>Forrásfájl</th>
                <th>Utolsó hiba</th>
              </tr>
            </thead>
            <tbody>
              {(data.dish_photos ?? []).map((row) => (
                <tr key={row.dish_id}>
                  <td>{row.dish_id} · {row.name_hu}</td>
                  <td>{row.thumb ? <img src={row.thumb} alt="" width={48} height={48} loading="lazy" /> : '—'}</td>
                  <td>{row.source_name ?? '—'}</td>
                  <td>{row.last_error ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  )
}
